import Axios from "axios";

// Everything here targets REACT_APP_MYFIT_API_URL (the Flask myfit-api).

class usersService {
    // Passwordless login — same two steps as myfit-app: email in, 6-digit
    // code out (sendLoginCode), then email+code in, session token out
    // (verifyLoginCode). Unlike the stubs below this does NOT swallow
    // errors, so the caller's .catch() can tell a real failure (bad email,
    // wrong code, expired code) from success.
    sendLoginCode(email) {
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/login`;
        return Axios.post(endPoint, { email });
    }

    verifyLoginCode(email, verifyDigits) {
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/verify`;
        return Axios.post(endPoint, { email, verify_digits: verifyDigits });
    }

    // First-login name collection — same /user/complete-profile call as
    // myfit-app's CompleteProfileScreen. Token comes straight from the
    // just-completed verify step's response, not localStorage, since the
    // caller (CompleteProfile) may run before AuthRoute would otherwise
    // consider the session "logged in".
    completeProfile(token, firstName, lastName) {
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/complete-profile`;
        return Axios.post(endPoint, {
            token,
            first_name: firstName,
            last_name: lastName,
        });
    }

    // /notifications — "Alerts" (RD: same thing as myfit-app's Alerts
    // page/Notification model). Read-only from here — notifications are
    // system-generated (the event job; see run_event_job() in app.py),
    // there's no user "add" flow the way the old vendor Reminders did.
    fetchNotifications() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/notifications`;
        return Axios.get(endPoint, { params: { token } });
    }

    markNotificationViewed(notificationId) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/notifications/${notificationId}/viewed`;
        return Axios.post(endPoint, { token });
    }

    // /tracking — myfit-api's Health Track feature, same capabilities as
    // myfit-app (types catalog, log/list/delete readings). Like the
    // passwordless login above, does NOT swallow errors.
    fetchHealthTrackTypes() {
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/health-track/types`;
        return Axios.get(endPoint);
    }

    fetchAccountPreferences() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/account-preferences`;
        return Axios.get(endPoint, { params: { token } });
    }

    updateAccountPreferences(updates) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/account-preferences`;
        return Axios.post(endPoint, { token, ...updates });
    }

    // Public catalog — no token, same as myfit-app. Never hardcode this
    // list on the client; it's meant to keep growing server-side.
    fetchHomeFeedCategories() {
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/home-feed/categories`;
        return Axios.get(endPoint);
    }

    fetchHomeFeedPreferences() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/home-feed-preferences`;
        return Axios.get(endPoint, { params: { token } });
    }

    // Full replace, not a merge — same as myfit-app's toggleFeedCategory().
    updateHomeFeedPreferences(categories) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/home-feed-preferences`;
        return Axios.post(endPoint, { token, categories });
    }

    fetchEventReminderSettings() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/event-reminders`;
        return Axios.get(endPoint, { params: { token } });
    }

    updateEventReminderSettings(updates) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/event-reminders`;
        return Axios.post(endPoint, { token, ...updates });
    }

    // Same 5 fields/endpoint as myfit-app's NotificationSettingsScreen —
    // GET returns the full set, POST accepts any subset to update.
    fetchNotificationSettings() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/notification-settings`;
        return Axios.get(endPoint, { params: { token } });
    }

    updateNotificationSettings(updates) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/notification-settings`;
        return Axios.post(endPoint, { token, ...updates });
    }

    fetchHealthRecords(type) {
        const token = localStorage.getItem("session_token");
        const params = { token };
        if (type) params.type = type;
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/health-records`;
        return Axios.get(endPoint, { params });
    }

    addHealthRecord(payload) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/health-records`;
        return Axios.post(endPoint, { token, ...payload });
    }

    deleteHealthRecord(recordId) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/health-records/${recordId}`;
        return Axios.delete(endPoint, { params: { token } });
    }

    // /events — myfit-api's Events feature, same capabilities as myfit-app
    // (list/add/delete, "for" self or a family member, repeat).
    fetchEvents() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/myevents`;
        return Axios.get(endPoint, { params: { token } });
    }

    addEvent(payload) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/myevents`;
        return Axios.post(endPoint, { token, ...payload });
    }

    deleteEvent(eventId) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/myevents/${eventId}`;
        return Axios.delete(endPoint, { params: { token } });
    }

    // Family members ("for" Self or a family member, on Add Event).
    fetchFamily() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/family`;
        return Axios.get(endPoint, { params: { token } });
    }

    // Settings > Connect QR — same 3 endpoints as myfit-app's own
    // ConnectQRScreen: a fresh single-use code to render as a QR image,
    // sharing preferences for what a scan actually shares, and the list
    // of accounts already connected via a past scan.
    createConnectCode() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/connect-code`;
        return Axios.post(endPoint, { token });
    }

    fetchConnectSharingPreferences() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/connect-sharing-preferences`;
        return Axios.get(endPoint, { params: { token } });
    }

    updateConnectSharingPreferences(updates) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/connect-sharing-preferences`;
        return Axios.post(endPoint, { token, ...updates });
    }

    fetchFamilyConnections() {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/family-connections`;
        return Axios.get(endPoint, { params: { token } });
    }

    // Sender-only (myfit-api enforces this too) — suspends/unsuspends an
    // account *this* user has shared their QR code with.
    suspendFamilyConnection(linkId, suspended) {
        const token = localStorage.getItem("session_token");
        const endPoint = `${process.env.REACT_APP_MYFIT_API_URL}/user/family-connections/${linkId}/suspend`;
        return Axios.post(endPoint, { token, suspended });
    }
}

export default usersService;
